/**
 * Reference buttons component for displaying navigation buttons in the Labels modal
 */
import React from "react"
import { Popover } from "antd"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faArrowUpRightFromSquare,
  faChevronDown,
} from "@fortawesome/free-solid-svg-icons"
import { LabelType, LabelItem } from "utils/labelUtils"

interface ReferenceButtonsProps {
  item: LabelItem
  scrollToLine?: (lineNumber: number) => void
  setIsModalVisible: (visible: boolean) => void
}

export const ReferenceButtons: React.FC<ReferenceButtonsProps> = ({
  item,
  scrollToLine,
  setIsModalVisible,
}) => {
  const handleNavigate = (lineNumber: number) => {
    if (scrollToLine) {
      scrollToLine(lineNumber)
      setIsModalVisible(false)
    }
  }

  if (item.type === LabelType.QUESTION) {
    // Question Labels: Show "Go to Question" button
    return scrollToLine && item.lineNumbers[0] ? (
      <button
        onClick={() => handleNavigate(item.lineNumbers[0])}
        className="text-xs px-2 py-1 bg-green-100 hover:bg-green-200 text-green-700 rounded border border-green-300 flex items-center gap-1 mx-auto"
      >
        Go to Question
        <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="text-[10px]" />
      </button>
    ) : (
      <span className="text-gray-400">-</span>
    )
  }

  if (item.type === LabelType.LIST || item.type === LabelType.RANGE_LIST) {
    const refCount = item.referenceLineNumbers?.length ?? 0

    // The line buttons live in a Popover (portal) so the cell stays compact and
    // the list scrolls independently of the virtual table.
    const refsPopover = (
      <div
        className="flex flex-col gap-1 max-h-[320px] overflow-y-auto pr-1"
        style={{ minWidth: 130 }}
      >
        {item.referenceLineNumbers?.map((lineNum, idx) => (
          <button
            key={idx}
            onClick={() => handleNavigate(lineNum)}
            className="text-xs px-2 py-1 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded border border-blue-300 flex items-center gap-1 justify-center"
          >
            Line {lineNum}
            <FontAwesomeIcon
              icon={faArrowUpRightFromSquare}
              className="text-[10px]"
            />
          </button>
        ))}
      </div>
    )

    return (
      <div className="flex flex-col gap-2 items-center w-full">
        {/* Go to Definition button */}
        {scrollToLine && item.lineNumbers[0] && (
          <button
            onClick={() => handleNavigate(item.lineNumbers[0])}
            className="text-xs px-2 py-1 bg-purple-100 hover:bg-purple-200 text-purple-700 rounded border border-purple-300 flex items-center gap-1 w-full justify-center"
          >
            Go to Definition
            <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="text-[10px]" />
          </button>
        )}
        {/* Reference count — opens a popover with the jump-to-line buttons */}
        {refCount > 0 ? (
          scrollToLine ? (
            <Popover
              content={refsPopover}
              title={`${refCount} reference${refCount !== 1 ? "s" : ""}`}
              trigger="click"
              placement="left"
            >
              <button className="text-xs px-2 py-1 bg-blue-50 hover:bg-blue-100 text-blue-700 font-semibold rounded border border-blue-300 flex items-center gap-1 w-full justify-center">
                {refCount} ref{refCount !== 1 ? "s" : ""}
                <FontAwesomeIcon icon={faChevronDown} className="text-[10px]" />
              </button>
            </Popover>
          ) : (
            <span className="text-blue-600 font-semibold">
              {refCount} ref{refCount !== 1 ? "s" : ""}
            </span>
          )
        ) : (
          <span className="text-gray-400">0 refs</span>
        )}
      </div>
    )
  }

  if (item.type === LabelType.EXCEPTIONS) {
    // Exceptions: Show "Go to Definition" button
    return scrollToLine && item.lineNumbers[0] ? (
      <button
        onClick={() => handleNavigate(item.lineNumbers[0])}
        className="text-xs px-2 py-1 bg-red-100 hover:bg-red-200 text-red-700 rounded border border-red-300 flex items-center gap-1 mx-auto"
      >
        Go to Definition
        <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="text-[10px]" />
      </button>
    ) : (
      <span className="text-gray-400">-</span>
    )
  }

  // Default: Show dash
  return <span className="text-gray-400">-</span>
}
